import React, { useEffect } from "react";
import { HiDotsHorizontal } from "react-icons/hi";
import { cloneDeep } from "lodash";

import { EAge, ICustomPoint, IFamilyMember } from "../models/IFamily";
import { ELCID, IPromisePoint } from "../models/IMediaToolkit";
import { SelectableButton } from "./SelectableButton";
import { Avatar } from "./Avatar";
import AAPService from "../services/AAPService";
import { MdDelete, MdSearch } from "react-icons/md";
import { ITranslations } from "../data/strings";

export interface IPromisePointPickerProps {
    id: number;
    point: IPromisePoint | ICustomPoint;
    familymembers: IFamilyMember[];
    defaultIcon?: string;
    readonly: boolean;
    scienceTitle: string;
    talktoTitle: string;
    lcid: ELCID;
    translations: ITranslations;
    updateAllMembers: (members: IFamilyMember[]) => void;
    updatePoint?: (point: IPromisePoint | ICustomPoint) => void;
    deletePoint?: () => void;
    editable?: boolean;
}

export const PromisePointPicker: React.FunctionComponent<IPromisePointPickerProps> = (props) => {
    const [expandDescription, setExpandDescription] = React.useState<boolean>(false);
    const additionalInfoRef = React.useRef<HTMLDivElement>(null);

    const pointTitle = decodeURIComponent(props.point.title);
    const pointDescription = decodeURIComponent(props.point.description || "");
    const pointAdditionalInfo = decodeURIComponent(props.point.additionalInfo || "");

    const allowPointDescription = pointDescription || pointAdditionalInfo;

    const pointIsDefault = (member: IFamilyMember) => {
        return (
            (props.point.targetAges || []).indexOf(member.age as EAge) > -1 ||
            (props.point.targetAges || []).indexOf(EAge.General) > -1
        );
    };

    const shouldPointBeSelected = (member: IFamilyMember) => {
        return (
            member.pointsSelected.indexOf(props.point.id) > -1 ||
            (pointIsDefault(member) && (member.pointsRemoved || []).indexOf(props.point.id) === -1)
        );
    };

    const isAddedOrRemoved = (member: IFamilyMember) => {
        return member.pointsSelected.indexOf(props.point.id) > -1 || member.pointsRemoved.indexOf(props.point.id) > -1;
    };

    const everyoneAdded =
        props.familymembers.filter((member) => shouldPointBeSelected(member)).length === props.familymembers.length;
    const isNotDefaults = props.familymembers.filter((member) => isAddedOrRemoved(member)).length > 0;
    const actionButtonClasses = `border border-aap-orange-500 text-aap-red-500 bg-white hover:bg-aap-red-500 hover:text-white text-sm py-1 px-2 rounded-sm sm:h-max flex-1 xl:flex-grow-0 lg:whitespace-nowrap`;

    useEffect(() => {
        if (additionalInfoRef.current) {
            // make all links a color and open in new tab
            const linkTags = additionalInfoRef.current.querySelectorAll("a");

            for (let i = 0; i < linkTags.length; i++) {
                const targLink = linkTags[i];
                targLink.classList.add(...["text-aap-red-500", "hover:text-aap-red-800", "underline", "font-bold"]);
                targLink.target = "_blank";
            }
        }
    });

    return (
        <div className="">
            <div
                className={`border p-3 border-aap-orange-500 rounded-md bg-white mb-3 overflow-hidden ${
                    expandDescription ? `shadow-lg shadow-aap-gray-300 mb-8` : ``
                }`}
            >
                <div className="flex gap-4 flex-1">
                    <div className="flex-grow xl:flex gap-4">
                        <div className="lg:flex xl:block flex-grow mb-5 gap-4">
                            <div className="title-and-icon flex gap-4 flex-grow">
                                <div
                                    className={`flex-shrink-0 flex-grow-0 w-10 h-10 mx-auto rounded-full bg-aap-red-500 border border-aap-orange-500 relative shadow-md shadow-aap-gray-400 content`}
                                >
                                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white">
                                        {props.point.isCustom ? (
                                            <HiDotsHorizontal size={28} />
                                        ) : (
                                            React.createElement(AAPService.GetMaterialIcon(props.point.icon || props.defaultIcon || 'undefined'), { size: 28 })
                                        )}
                                    </div>
                                </div>
                                <div className="flex-grow gap-4 lg:flex xl:block">
                                    {props.editable && !props.readonly ? (
                                        <input
                                            id={`promise-point-editabletitle-${props.id}`}
                                            key={`promise-point-editabletitle-${props.id}`}
                                            value={pointTitle}
                                            onChange={(ev) => {
                                                const tempPoint = cloneDeep(props.point);
                                                tempPoint.title = ev.target.value;
                                                if (props.updatePoint) {
                                                    props.updatePoint(tempPoint);
                                                }
                                            }}
                                            type="text"
                                            className="mt-1 rounded-sm border border-aap-gray-200 focus:border-aap-green-500 text-lg p-2 w-full transition-all h-12 flex-grow"
                                        />
                                    ) : (
                                        <h4 className="text-lg mb-1 flex-grow">{pointTitle}</h4>
                                    )}
                                    <div className="mt-2 flex gap-2 action-buttons flex-shrink-0 content-start lg:w-64 lg:flex-wrap xl:flex-grow-0 xl:w-auto">
                                        {props.point.isCustom && props.editable && !props.readonly ? (
                                            <button
                                                type="button"
                                                onClick={props.deletePoint}
                                                className={`${actionButtonClasses}`}
                                                id={`btn_promisePoint_deleteCustom_${ELCID[props.lcid]}`}
                                            >
                                                <MdDelete size={16} className="inline" /> {props.translations.remove}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setExpandDescription(!expandDescription)}
                                                id={`btn_promisePoint_reasonTips_${expandDescription ? 'Close' : 'Expand'}_${ELCID[props.lcid]}`}
                                                data-promisePointTitle={pointTitle}
                                                className={`${actionButtonClasses} ${
                                                    allowPointDescription === "" ? `opacity-0 pointer-events-none` : ``
                                                }`}
                                            >
                                                {expandDescription ? props.translations.close : props.translations.reasonsTips}
                                            </button>
                                        )}
                                        {!props.readonly ? (
                                            <button
                                                type="button"
                                                className={`${actionButtonClasses}`}
                                                id={`btn_promisePoint_selectAllMembers_${ELCID[props.lcid]}`}
                                                data-promisePointCustom={props.point.isCustom}
                                                data-promisePointTitle={pointTitle}
                                                onClick={() => {
                                                    const tempMembers = cloneDeep(props.familymembers);
                                                    tempMembers.forEach((tempMember, memberIdx) => {
                                                        const addedIndex = tempMember.pointsSelected.indexOf(
                                                            props.point.id
                                                        );
                                                        const removedIndex = tempMember.pointsRemoved.indexOf(
                                                            props.point.id
                                                        );
                                                        if (everyoneAdded) {
                                                            // remove it from everyone
                                                            if (addedIndex > -1) {
                                                                tempMember.pointsSelected.splice(addedIndex, 1);
                                                            }
                                                            // if non-custom, add it to removed
                                                            if (!props.point.isCustom) {
                                                                if (removedIndex === -1) {
                                                                    tempMember.pointsRemoved.push(props.point.id);
                                                                }
                                                            }
                                                        } else {
                                                            if (addedIndex === -1) {
                                                                tempMember.pointsSelected.push(props.point.id);
                                                            }
                                                            // if non-custom, remove it to removed
                                                            if (!props.point.isCustom) {
                                                                if (removedIndex > -1) {
                                                                    tempMember.pointsRemoved.splice(removedIndex, 1);
                                                                }
                                                            }
                                                        }
                                                    });
                                                    props.updateAllMembers(tempMembers);
                                                }}
                                            >
                                                {everyoneAdded ? props.translations.unselectAll : props.translations.selectAll}
                                            </button>
                                        ) : null}
                                        {isNotDefaults && !props.point.isCustom && !props.readonly ? (
                                            <button
                                                className={`${actionButtonClasses}`}
                                                type="button"
                                                title={props.translations.resetToAAPRecs}
                                                id={`btn_promisePoint_resetDefaultMemberSelections_${ELCID[props.lcid]}`}
                                                data-promisePointTitle={pointTitle}
                                                onClick={() => {
                                                    const tempMembers = cloneDeep(props.familymembers);
                                                    tempMembers.forEach((tempMember, memberIdx) => {
                                                        tempMember.pointsSelected = tempMember.pointsSelected.filter(
                                                            (pointId) => pointId !== props.point.id
                                                        );
                                                        tempMember.pointsRemoved = tempMember.pointsRemoved.filter(
                                                            (pointId) => pointId !== props.point.id
                                                        );
                                                    });
                                                    props.updateAllMembers(tempMembers);
                                                }}
                                            >
                                                {props.translations.reset}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex-grow-0 mt-1 mr-2">
                            <div className="flex gap-4 flex-wrap xl:max-w-lg xl:justify-end">
                                {props.familymembers.map((member, memberIdx) => {
                                    return (
                                        <SelectableButton
                                            checked={shouldPointBeSelected(member)}
                                            key={`point-${pointTitle}-button-${memberIdx}`}
                                            id={`btn_promisePoint_memberSelect_${ELCID[props.lcid]}`}
                                            data-memberAge={member.age}
                                            data-action={shouldPointBeSelected(member) ? `unselect` : `select`}
                                            additionalClasses={`h-15 flex gap-2 flex-1 md:flex-0 lg:flex-1 xl:flex-0`}
                                            readonly={props.readonly}
                                            onClick={() => {
                                                const tempMembers = cloneDeep(props.familymembers);
                                                const tempMember = tempMembers[memberIdx];
                                                let adding = true;

                                                // see if its a default point
                                                // default points should *only* be added to the member.pointsRemoved if its unselected
                                                if (pointIsDefault(member)) {
                                                    if (tempMember.pointsRemoved.indexOf(props.point.id) > -1) {
                                                        // its already removed (within the pointsRemoved array), remove the removal
                                                        tempMember.pointsRemoved = tempMember.pointsRemoved.filter(
                                                            (pointId) => props.point.id !== pointId
                                                        );
                                                    } else {
                                                        tempMember.pointsRemoved.push(props.point.id);
                                                    }
                                                } else {
                                                    if (tempMember.pointsSelected.indexOf(props.point.id) > -1) {
                                                        // already exists
                                                        tempMember.pointsSelected = tempMember.pointsSelected.filter(
                                                            (pointId) => props.point.id !== pointId
                                                        );
                                                        adding = false;
                                                    } else {
                                                        // new
                                                        tempMember.pointsSelected.push(props.point.id);
                                                    }
                                                    // if its not a custom point, figure out the pointsRemoved piece
                                                    if (!props.point.isCustom) {
                                                        if (adding) {
                                                            if (tempMember.pointsRemoved.indexOf(props.point.id) > -1) {
                                                                tempMember.pointsRemoved = tempMember.pointsRemoved.filter(
                                                                    (pointId) => props.point.id !== pointId
                                                                );
                                                            }
                                                        } else {
                                                            tempMember.pointsRemoved.push(props.point.id);
                                                        }
                                                    }
                                                }
                                                props.updateAllMembers(tempMembers);
                                            }}
                                            variant={`child`}
                                        >
                                            <Avatar
                                                avatarId={member.avatar}
                                                uniqueKey={`point-${pointTitle}-button-${memberIdx}-avatar-${member.name}`}
                                                size={48}
                                                additionalClasses={`bg-white`}
                                                lcid={props.lcid}
                                                translations={props.translations}
                                            />
                                            <div className="text-left" title={member.age?.toString()}>
                                                <div className="pt-3 lg:pt-0 lg:text-lg whitespace-nowrap">{member.name}</div>
                                                <div className="text-xs whitespace-nowrap hidden lg:block">
                                                    {props.translations[member.age || '']}
                                                </div>
                                            </div>
                                        </SelectableButton>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                {expandDescription ? (
                    <div className="relative mt-3 -mb-3 -mx-3 px-9 bg-aap-yellow-500 lg:flex justify-evenly gap-6 overflow-hidden">
                        <MdSearch size={128} className={`absolute top-2 -left-10 opacity-10`} />
                        {pointDescription ? (
                            <div className="py-6 basis-1/2 grow shrink-0 additionalInfos">
                                <h5 className="font-bold text-sm mb-2">{props.scienceTitle || 'The science behind it...'}</h5>
                                <p className="text-xs" dangerouslySetInnerHTML={{ __html: pointDescription }}></p>
                            </div>
                        ) : null}
                        {pointAdditionalInfo ? (
                            <div className="py-6 basis-1/2 grow shrink-0 border-t border-t-aap-gray-200 lg:border-t-0 additionalInfos">
                                <h5 className="font-bold text-sm mb-2">{props.talktoTitle || 'What to say to my kids...'}</h5>
                                <p
                                    className="text-xs"
                                    dangerouslySetInnerHTML={{ __html: pointAdditionalInfo }}
                                    ref={additionalInfoRef}
                                ></p>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
